import { z } from 'zod';

import type { BudgetPayrollScenarioResponse } from './fpa-payroll.js';
import type {
  BudgetCommitmentSource,
  BudgetCommitmentStatus,
  BudgetDimensionType,
  BudgetEnforcementMode,
  BudgetExceptionRequestType,
  BudgetExceptionResolutionType,
  BudgetExceptionStatus,
  BudgetHistoryAction,
  BudgetHistoryEntityType,
  BudgetPlanStatus,
  BudgetRecomputeStatus,
  BudgetStatus,
  BudgetVersionKind,
  FpaForecastRuleVariant,
} from './generated/enums.js';

export const BUDGET_PAYROLL_PROJECTION_UNAVAILABLE =
  'BUDGET_PAYROLL_PROJECTION_UNAVAILABLE' as const;
export const BUDGET_PAYROLL_PROJECTION_CONFIDENTIAL =
  'BUDGET_PAYROLL_PROJECTION_CONFIDENTIAL' as const;
export const BUDGET_PAYROLL_PROJECTION_MULTIPLE_RULE_SETS =
  'BUDGET_PAYROLL_PROJECTION_MULTIPLE_RULE_SETS' as const;
export const BUDGET_PAYROLL_PROJECTION_CURRENCY_MISMATCH =
  'BUDGET_PAYROLL_PROJECTION_CURRENCY_MISMATCH' as const;
export const BUDGET_PAYROLL_PROJECTION_RUN_TYPE_UNAVAILABLE =
  'BUDGET_PAYROLL_PROJECTION_RUN_TYPE_UNAVAILABLE' as const;

export const BUDGET_PAYROLL_PROJECTION_CODES = [
  BUDGET_PAYROLL_PROJECTION_UNAVAILABLE,
  BUDGET_PAYROLL_PROJECTION_CONFIDENTIAL,
  BUDGET_PAYROLL_PROJECTION_MULTIPLE_RULE_SETS,
  BUDGET_PAYROLL_PROJECTION_CURRENCY_MISMATCH,
  BUDGET_PAYROLL_PROJECTION_RUN_TYPE_UNAVAILABLE,
  'UNATTRIBUTABLE',
  'FILTER_REFERENCE_UNRESOLVED',
  'HEADCOUNT_PLAN_UNATTRIBUTED',
  'ENCARGOS_JURISDICTION_UNSUPPORTED',
  'ENCARGOS_POLICY_NOT_EFFECTIVE',
  'JOINERS_NOT_MODELLED',
  'UNRECOGNISED_RUBRIC',
  'PAYROLL_MANAGERIAL_UNRESOLVED',
  'UNMAPPED_COLLABORATOR',
  'PAYROLL_BASE_RUN_STALE',
  'PAYROLL_TERMINATION_IDENTITY_AMBIGUOUS',
  'PAYROLL_CURRENCY_MISMATCH',
  'PAYROLL_PROVISION_MISSING',
  'PAYROLL_EMPLOYEE_SKIPPED',
  'INSUFFICIENT_HISTORY',
  'NO_SOURCE_DATA',
  'UNAVAILABLE',
  'PAYROLL_COLLABORATOR_STALE',
  'PAYROLL_COLLABORATOR_NO_EMISSION',
  'PAYROLL_REFUSED_COVERED_BY_RESIDUAL',
  'PAYROLL_TEMPLATE_NOT_MODELLED',
  'PAYROLL_TEMPLATE_WINDOW_DRIFTED',
] as const;

export type BudgetPayrollProjectionCode = (typeof BUDGET_PAYROLL_PROJECTION_CODES)[number];

export type BudgetPayrollProjectionRule = Extract<
  FpaForecastRuleVariant,
  'PAYROLL_RUNRATE' | 'HEADCOUNT_PLAN'
>;

/** Aggregated-only projection metadata. It must never contain employee-level data. */
export interface BudgetProjectedBreakdown {
  adHoc: number;
  payroll: number;
}

export interface BudgetPayrollProjectionMetadata {
  projectedBreakdown: BudgetProjectedBreakdown;
  payrollProjectionRule: BudgetPayrollProjectionRule | null;
  /** FP&A scenario used for the published payroll projection. */
  payrollProjectionRuleSetId: string | null;
  /** SHA-256 marker for the exact rule-set revision used by the read. */
  payrollProjectionRuleSetRevision: string | null;
  payrollProjectionCodes: BudgetPayrollProjectionCode[];
  /** Number of non-zero line/competency aggregates hidden by payroll confidentiality. */
  hiddenPayrollProjectionCount: number;
}

export const BudgetGlobalSettingsSchema = z.object({
  designatedBudgetBookId: z.string().uuid().nullable(),
  defaultEnforcementMode: z.enum(['HARD_BLOCK', 'SOFT_BLOCK', 'ALERT_ONLY']),
  alertThresholdWarning: z.number().int().min(0).max(100),
  alertThresholdCritical: z.number().int().min(0).max(100),
  blockEnforcementWhileStale: z.boolean(),
  unmatchedSpendPolicy: z.enum(['BLOCK', 'REQUIRE_EXCEPTION', 'ALERT_ONLY']),
});

export type BudgetGlobalSettings = z.infer<typeof BudgetGlobalSettingsSchema>;

export interface BudgetResponse {
  payrollScenario?: BudgetPayrollScenarioResponse;
  payrollForecastRuleSetId?: string | null;
  payrollScenarioOrigin?: 'EXPLICIT' | 'ENTITY_DEFAULT';
  tenantId: string;
  budgetId: string;
  name: string;
  description: string | null;
  entityId: string;
  bookId: string;
  status: BudgetPlanStatus;
  startPeriodId: string;
  endPeriodId: string | null;
  /** Derived from startPeriod.startDate */
  effectiveFrom: string;
  /** Derived from endPeriod.endDate; null when endPeriodId is null */
  effectiveUntil: string | null;
  sourceBudgetId: string | null;
  versionKind: BudgetVersionKind;
  /** Family root budgetId; null when this budget is its own family root. */
  versionFamilyId: string | null;
  versionNumber: number;
  createdAt: string;
  updatedAt: string | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  /** Last material preparer of a REVISION (display). Control reads the set. */
  reforecastPreparedByUserId?: string | null;
  /** Users who created the revision or made a real material change. */
  reforecastPreparerUserIds?: string[];
  ownerUserId: string | null;
  ownerName?: string | null;
  entityName?: string | null;
  bookName?: string | null;
  lineCount?: number;
  totalLimit?: number;
  totalUtilized?: number;
  isActive?: boolean;
  recomputeStatus?: BudgetRecomputeStatus;
  lastRecomputedAt?: string | null;
  recomputeWarnings?: BudgetRecomputeWarning[];
  /** ADR 0013: the ACTIVE this promoted Reforecast replaced. */
  supersededBudgetId?: string | null;
  supersededBudgetName?: string | null;
  promotedAt?: string | null;
  promotedByUserId?: string | null;
  promotedByUserName?: string | null;
}

export interface BudgetResyncResponse {
  jobId: string;
  enqueued: boolean;
  recomputeStatus: BudgetRecomputeStatus;
}

export interface BudgetHistoryActorResponse {
  userId: string | null;
  name: string;
}

export interface BudgetHistoryResponse {
  tenantId: string;
  historyId: string;
  budgetId: string;
  entityType: BudgetHistoryEntityType;
  entityId: string;
  action: BudgetHistoryAction;
  performedByUserId: string | null;
  actor: BudgetHistoryActorResponse;
  changes: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreateBudgetRequest {
  /** Omit to inherit the persisted entity payroll default. */
  payrollForecastRuleSetId?: string | null;
  name: string;
  description?: string | null;
  entityId: string;
  bookId?: string;
  startPeriodId: string;
  endPeriodId?: string | null;
}

export type CreateBudgetTemplateScope = 'COST_CENTER' | 'DEPARTMENT' | 'MANAGERIAL_ACCOUNT';

export interface CreateBudgetWithTemplateRequest extends Omit<CreateBudgetRequest, 'endPeriodId'> {
  endPeriodId: string;
  templateScope: CreateBudgetTemplateScope;
  currencyCode?: string;
  enforcementMode?: BudgetEnforcementMode;
  perPeriodEnforcementMode?: BudgetEnforcementMode | null;
  alertThresholdWarning?: number | null;
  alertThresholdCritical?: number | null;
}

export interface UpdateBudgetRequest {
  name?: string;
  description?: string | null;
  startPeriodId?: string;
  endPeriodId?: string | null;
}

export interface TransferBudgetOwnerRequest {
  ownerUserId: string;
}

export const BUDGET_OWNER_MISSING_PERMISSION_CODE = 'BUDGET_OWNER_MISSING_PERMISSION' as const;

export interface DuplicateBudgetRequest {
  name: string;
  description?: string | null;
  startPeriodId?: string;
  endPeriodId?: string | null;
}

export interface ActivateBudgetRequest {
  confirmSwitchActiveBudget?: boolean;
}

/**
 * Creates a manually revised version ("Reforecast") of a budget inside the same
 * version family (ADR 0010). The revision starts as a DRAFT copy of the source
 * version's lines/periods. `activateBudget` still refuses it
 * (`BUDGET_REVISION_NOT_ACTIVATABLE`); ADR 0013 promotion is the only path
 * that may make a revision the enforcing ACTIVE.
 */

export interface BudgetReforecastPromotionPeriodDelta {
  periodId: string;
  /** Prisma.Decimal string — previous ACTIVE limit for the period. */
  previousAmount: string;
  /** Prisma.Decimal string — revision limit for the period. */
  nextAmount: string;
}

/** Approve exactly the revision state returned by the promotion preview. */
export interface PromoteBudgetReforecastRequest {
  expectedRevisionFingerprint: string;
}

/** Preview for the Reforecast approval dialog (ADR 0013). */
export interface BudgetReforecastPromotionPreview {
  revisionBudgetId: string;
  revisionName: string;
  revisionVersionNumber: number;
  /** SHA-256 fingerprint of the reviewed revision and its material line state. */
  revisionFingerprint: string;
  currentActiveBudgetId: string | null;
  currentActiveName: string | null;
  missingPeriodIds: string[];
  periods: BudgetReforecastPromotionPeriodDelta[];
  /** Material authors who cannot approve this Reforecast themselves. */
  preparerUserIds: string[];
  /** Read-only estimate of the utilization state after the pointer moves. */
  utilizationImpact: {
    currentActiveTotalUtilized: number;
    currentActiveTotalLimit: number;
    reforecastTotalLimit: number;
    projectedUtilizationPct: number;
  };
}
export interface CreateBudgetRevisionRequest {
  /** Version label, e.g. "Reforecast ago–dez/26". Stored as the budget name. */
  name: string;
  description?: string | null;
  startPeriodId?: string;
  endPeriodId?: string | null;
}

/** One per-period, per-dimension budgeted amount of a budget version. */
export interface BudgetVersionScenarioCell {
  budgetLineId: string;
  periodId: string;
  dimensionType: BudgetDimensionType;
  costCenterId: string | null;
  departmentId: string | null;
  managerialAccountId: string | null;
  glAccountId: string | null;
  segmentId: string | null;
  segmentValueId: string | null;
  isFallback: boolean;
  amount: number;
  /**
   * The same amount as a decimal string with no float round-trip (Prisma.Decimal →
   * toString). FP&A scenario columns consume this field (ALC-1907); `amount` stays
   * for existing numeric consumers.
   */
  amountDecimal: string;
  currencyCode: string;
}

/**
 * A budget version's amounts by period × dimension, shaped to be consumable as a
 * DRE scenario column. `budgetId` is the scenario reference identifier (the FK
 * agreed with the scenario axis slice).
 */
export interface BudgetVersionScenarioResponse {
  budgetId: string;
  /** Family root budgetId (equals `budgetId` for the family root itself). */
  versionFamilyId: string;
  versionKind: BudgetVersionKind;
  versionNumber: number;
  /** Version label (budget name). */
  label: string;
  entityId: string;
  bookId: string;
  status: BudgetPlanStatus;
  effectiveFrom: string;
  effectiveUntil: string | null;
  periodIds: string[];
  cells: BudgetVersionScenarioCell[];
}

export const BUDGET_ACTIVATION_SWITCH_REQUIRED_CODE = 'BUDGET_ACTIVATION_SWITCH_REQUIRED' as const;

export const BUDGET_INVALID_GL_ACCOUNT_CODE = 'BUDGET_INVALID_GL_ACCOUNT' as const;

/** Stable code for transient contention while an authoritative budget gate is acquiring locks. */
export const BUDGET_CONCURRENCY_RETRY_CODE = 'BUDGET_CONCURRENCY_RETRY' as const;

/** Stable non-retriable code when a rematch destination disappeared from the locked scope. */
export const BUDGET_ATTRIBUTION_DESTINATION_MISSING_CODE =
  'BUDGET_ATTRIBUTION_DESTINATION_MISSING' as const;

export const BUDGET_ATTRIBUTION_BUDGET_SCOPE_MISMATCH_CODE =
  'BUDGET_ATTRIBUTION_BUDGET_SCOPE_MISMATCH' as const;

export const BUDGET_CONCURRENCY_RETRY_MESSAGE_PT_BR =
  'O orçamento está sendo atualizado. Tente novamente em instantes.';

export const BUDGET_RESYNC_QUEUE_UNAVAILABLE_CODE = 'BUDGET_RESYNC_QUEUE_UNAVAILABLE' as const;
export const BUDGET_RESYNC_BOOK_SCOPE_MISMATCH_CODE = 'BUDGET_RESYNC_BOOK_SCOPE_MISMATCH' as const;
export const BUDGET_RESYNC_BUDGET_NOT_FOUND_CODE = 'BUDGET_RESYNC_BUDGET_NOT_FOUND' as const;
export const BUDGET_RESYNC_BUDGET_NOT_ACTIVE_CODE = 'BUDGET_RESYNC_BUDGET_NOT_ACTIVE' as const;
export const BUDGET_RESYNC_ENTITY_SCOPE_MISMATCH_CODE =
  'BUDGET_RESYNC_ENTITY_SCOPE_MISMATCH' as const;
export const BUDGET_RESYNC_JOB_BOOK_SCOPE_MISMATCH_CODE =
  'BUDGET_RESYNC_JOB_BOOK_SCOPE_MISMATCH' as const;
export const BUDGET_RESYNC_RECURRING_SYNC_INCOMPLETE_CODE =
  'BUDGET_RESYNC_RECURRING_SYNC_INCOMPLETE' as const;
export const BUDGET_RESYNC_WORKER_FAILED_CODE = 'BUDGET_RESYNC_WORKER_FAILED' as const;

export const BUDGET_ATTRIBUTION_DESTINATION_MISSING_MESSAGE_PT_BR =
  'Uma linha de destino da atribuição orçamentária não existe mais. Execute a reconciliação do orçamento ou contate o suporte.';

export const BUDGET_ATTRIBUTION_BUDGET_SCOPE_MISMATCH_MESSAGE_PT_BR =
  'Uma atribuição orçamentária ficou fora do escopo bloqueado. Contate o suporte para verificar a configuração do orçamento.';

/** Stable API error codes for budget validation — prefer matching on `code` in clients. */
export const BudgetValidationCode = {
  /** Gate-aware recurring template save refused: the resolved budget's recompute is stale. */
  BUDGET_RECURRING_BLOCKED_WHILE_STALE: 'BUDGET_RECURRING_BLOCKED_WHILE_STALE',
  BUDGET_BOOK_REQUIRED: 'BUDGET_BOOK_REQUIRED',
  BUDGET_BOOK_NOT_LINKED: 'BUDGET_BOOK_NOT_LINKED',
  BUDGET_EFFECTIVE_RANGE_INVALID: 'BUDGET_EFFECTIVE_RANGE_INVALID',
  BUDGET_EFFECTIVE_UNTIL_REQUIRED: 'BUDGET_EFFECTIVE_UNTIL_REQUIRED',
  BUDGET_END_PERIOD_REQUIRED: 'BUDGET_END_PERIOD_REQUIRED',
  BUDGET_ARCHIVED: 'BUDGET_ARCHIVED',
  BUDGET_RESYNC_REQUIRES_ACTIVE: 'BUDGET_RESYNC_REQUIRES_ACTIVE',
  BUDGET_RESYNC_REQUIRES_DESIGNATED_BOOK: 'BUDGET_RESYNC_REQUIRES_DESIGNATED_BOOK',
  BUDGET_ARCHIVED_ACTIVATE: 'BUDGET_ARCHIVED_ACTIVATE',
  BUDGET_DEACTIVATE_BEFORE_ARCHIVE: 'BUDGET_DEACTIVATE_BEFORE_ARCHIVE',
  BUDGET_UNSUPPORTED_TEMPLATE_SCOPE: 'BUDGET_UNSUPPORTED_TEMPLATE_SCOPE',
  BUDGET_PER_PERIOD_ENFORCEMENT_REQUIRES_LIMIT: 'BUDGET_PER_PERIOD_ENFORCEMENT_REQUIRES_LIMIT',
  BUDGET_ALERT_THRESHOLD_WARNING_RANGE: 'BUDGET_ALERT_THRESHOLD_WARNING_RANGE',
  BUDGET_ALERT_THRESHOLD_CRITICAL_RANGE: 'BUDGET_ALERT_THRESHOLD_CRITICAL_RANGE',
  BUDGET_ALERT_THRESHOLD_ORDER: 'BUDGET_ALERT_THRESHOLD_ORDER',
  BUDGET_NO_PERIODS_IN_EFFECTIVE_RANGE: 'BUDGET_NO_PERIODS_IN_EFFECTIVE_RANGE',
  BUDGET_CONTIGUOUS_EFFECTIVE_PERIODS: 'BUDGET_CONTIGUOUS_EFFECTIVE_PERIODS',
  BUDGET_NO_ACTIVE_COST_CENTERS: 'BUDGET_NO_ACTIVE_COST_CENTERS',
  BUDGET_NO_ACTIVE_DEPARTMENTS: 'BUDGET_NO_ACTIVE_DEPARTMENTS',
  BUDGET_NO_ACTIVE_MANAGERIAL_ACCOUNTS: 'BUDGET_NO_ACTIVE_MANAGERIAL_ACCOUNTS',
  BUDGET_FALLBACK_LINE_SCOPE_LOCKED: 'BUDGET_FALLBACK_LINE_SCOPE_LOCKED',
  BUDGET_COST_CENTER_DIMENSION_INVALID: 'BUDGET_COST_CENTER_DIMENSION_INVALID',
  BUDGET_DEPARTMENT_DIMENSION_INVALID: 'BUDGET_DEPARTMENT_DIMENSION_INVALID',
  BUDGET_COMPANY_DIMENSION_INVALID: 'BUDGET_COMPANY_DIMENSION_INVALID',
  BUDGET_AMOUNT_NON_NEGATIVE: 'BUDGET_AMOUNT_NON_NEGATIVE',
  BUDGET_AMOUNT_POSITIVE: 'BUDGET_AMOUNT_POSITIVE',
  BUDGET_AMOUNT_INVALID_SCALE: 'BUDGET_AMOUNT_INVALID_SCALE',
  BUDGET_PER_PERIOD_AMOUNT_POSITIVE: 'BUDGET_PER_PERIOD_AMOUNT_POSITIVE',
  BUDGET_PERIOD_LIMITS_MISSING_PERIOD_ID: 'BUDGET_PERIOD_LIMITS_MISSING_PERIOD_ID',
  BUDGET_PERIOD_LIMITS_DUPLICATE_PERIOD_ID: 'BUDGET_PERIOD_LIMITS_DUPLICATE_PERIOD_ID',
  BUDGET_PERIOD_LIMITS_INVALID_AMOUNT: 'BUDGET_PERIOD_LIMITS_INVALID_AMOUNT',
  BUDGET_PERIOD_LIMITS_NEGATIVE_AMOUNT: 'BUDGET_PERIOD_LIMITS_NEGATIVE_AMOUNT',
  BUDGET_PERIOD_LIMITS_OUTSIDE_WINDOW: 'BUDGET_PERIOD_LIMITS_OUTSIDE_WINDOW',
  BUDGET_STAMPED_PERIOD_CONFLICT: 'BUDGET_STAMPED_PERIOD_CONFLICT',
  BUDGET_ID_REQUIRED: 'BUDGET_ID_REQUIRED',
  BUDGET_ARCHIVED_LINES: 'BUDGET_ARCHIVED_LINES',
  BUDGET_LINE_ENTITY_BOOK_MISMATCH: 'BUDGET_LINE_ENTITY_BOOK_MISMATCH',
  BUDGET_INVALID_COST_CENTER: 'BUDGET_INVALID_COST_CENTER',
  BUDGET_INVALID_DEPARTMENT: 'BUDGET_INVALID_DEPARTMENT',
  BUDGET_COST_CENTER_INACTIVE: 'BUDGET_COST_CENTER_INACTIVE',
  BUDGET_DEPARTMENT_INACTIVE: 'BUDGET_DEPARTMENT_INACTIVE',
  BUDGET_INVALID_MANAGERIAL_ACCOUNT: 'BUDGET_INVALID_MANAGERIAL_ACCOUNT',
  BUDGET_SEGMENT_VALUE_REQUIRES_SEGMENT: 'BUDGET_SEGMENT_VALUE_REQUIRES_SEGMENT',
  BUDGET_INVALID_CUSTOM_SEGMENT: 'BUDGET_INVALID_CUSTOM_SEGMENT',
  BUDGET_INVALID_CUSTOM_SEGMENT_VALUE: 'BUDGET_INVALID_CUSTOM_SEGMENT_VALUE',
  BUDGET_LINE_SEGMENT_HAS_TEXT_VALUES: 'BUDGET_LINE_SEGMENT_HAS_TEXT_VALUES',
  BUDGET_LINE_PERIOD_INVALID: 'BUDGET_LINE_PERIOD_INVALID',
  BUDGET_START_AFTER_END_PERIOD: 'BUDGET_START_AFTER_END_PERIOD',
  BUDGET_NO_PERIODS_IN_LINE_RANGE: 'BUDGET_NO_PERIODS_IN_LINE_RANGE',
  BUDGET_CONTIGUOUS_LINE_PERIODS: 'BUDGET_CONTIGUOUS_LINE_PERIODS',
  BUDGET_TOTAL_BELOW_PERIOD_LIMITS: 'BUDGET_TOTAL_BELOW_PERIOD_LIMITS',
  BUDGET_TOTAL_LIMIT_CANNOT_BE_CLEARED: 'BUDGET_TOTAL_LIMIT_CANNOT_BE_CLEARED',
  BUDGET_FALLBACK_LINE_CANNOT_ARCHIVE: 'BUDGET_FALLBACK_LINE_CANNOT_ARCHIVE',
  BUDGET_SELECT_LINES_TO_UPDATE: 'BUDGET_SELECT_LINES_TO_UPDATE',
  BUDGET_SELECT_LINES_TO_ARCHIVE: 'BUDGET_SELECT_LINES_TO_ARCHIVE',
  BUDGET_CHOOSE_FIELDS_TO_UPDATE: 'BUDGET_CHOOSE_FIELDS_TO_UPDATE',
  BUDGET_ARCHIVED_LINE: 'BUDGET_ARCHIVED_LINE',
  BUDGET_CURRENCY_BRL_ONLY: 'BUDGET_CURRENCY_BRL_ONLY',
  BUDGET_TARGET_OWNER_INVALID: 'BUDGET_TARGET_OWNER_INVALID',
  BUDGET_ACTIVATE_ZERO_AMOUNT_LINES: 'BUDGET_ACTIVATE_ZERO_AMOUNT_LINES',
  BUDGET_REVISION_NOT_ACTIVATABLE: 'BUDGET_REVISION_NOT_ACTIVATABLE',
  BUDGET_REFORECAST_PARTIAL_COVERAGE: 'BUDGET_REFORECAST_PARTIAL_COVERAGE',
  BUDGET_REFORECAST_NOT_PROMOTABLE: 'BUDGET_REFORECAST_NOT_PROMOTABLE',
  BUDGET_REFORECAST_NO_ACTIVE: 'BUDGET_REFORECAST_NO_ACTIVE',
  BUDGET_REFORECAST_PROMOTION_CONFLICT: 'BUDGET_REFORECAST_PROMOTION_CONFLICT',
  BUDGET_REFORECAST_SELF_APPROVAL: 'BUDGET_REFORECAST_SELF_APPROVAL',
  BUDGET_REFORECAST_PREPARER_REQUIRED: 'BUDGET_REFORECAST_PREPARER_REQUIRED',
  BUDGET_REFORECAST_ENFORCING_CANNOT_DEACTIVATE: 'BUDGET_REFORECAST_ENFORCING_CANNOT_DEACTIVATE',
  BUDGET_SUPERSEDED_NOT_ACTIVATABLE: 'BUDGET_SUPERSEDED_NOT_ACTIVATABLE',
  BUDGET_SUPERSEDED_IMMUTABLE: 'BUDGET_SUPERSEDED_IMMUTABLE',
  OWNER_MISSING_PERMISSION: BUDGET_OWNER_MISSING_PERMISSION_CODE,
  OWNER_TRANSFER_FORBIDDEN: 'BUDGET_OWNER_TRANSFER_FORBIDDEN',
  LINE_OWNER_MODIFY_FORBIDDEN: 'BUDGET_LINE_OWNER_MODIFY_FORBIDDEN',
  LINE_OWNER_LIMIT_EXCEEDED: 'BUDGET_LINE_OWNER_LIMIT_EXCEEDED',
  HIERARCHY_AMOUNT_EXCEEDED: 'BUDGET_HIERARCHY_AMOUNT_EXCEEDED',
  INVALID_GL_ACCOUNT: BUDGET_INVALID_GL_ACCOUNT_CODE,
  // ALC-2946 M15 error-conformance follow-up: every codeless
  // `new BudgetValidationError(...)` throw site in budget.service.ts got a
  // stable code here so clients never fall back to a generic message.
  BUDGET_BULK_AMOUNT_CANNOT_BE_CLEARED: 'BUDGET_BULK_AMOUNT_CANNOT_BE_CLEARED',
  BUDGET_BULK_AMOUNT_INVALID_NUMBER: 'BUDGET_BULK_AMOUNT_INVALID_NUMBER',
  BUDGET_BULK_AMOUNT_MUST_BE_POSITIVE: 'BUDGET_BULK_AMOUNT_MUST_BE_POSITIVE',
  BUDGET_BULK_AMOUNT_UNSUPPORTED_OPERATION: 'BUDGET_BULK_AMOUNT_UNSUPPORTED_OPERATION',
  BUDGET_BULK_AMOUNT_RESULT_NOT_POSITIVE: 'BUDGET_BULK_AMOUNT_RESULT_NOT_POSITIVE',
  BUDGET_EXCEPTION_TARGET_REQUIRED: 'BUDGET_EXCEPTION_TARGET_REQUIRED',
  BUDGET_EXCEPTION_NOT_BLOCKED: 'BUDGET_EXCEPTION_NOT_BLOCKED',
  BUDGET_EXCEPTION_NO_EXCESS: 'BUDGET_EXCEPTION_NO_EXCESS',
  BUDGET_EXCEPTION_RECURRING_REQUIRES_LIMIT_INCREASE:
    'BUDGET_EXCEPTION_RECURRING_REQUIRES_LIMIT_INCREASE',
  BUDGET_EXCEPTION_ALREADY_RESOLVED: 'BUDGET_EXCEPTION_ALREADY_RESOLVED',
  BUDGET_EXCEPTION_RECURRING_LIMIT_INCREASE_ONLY: 'BUDGET_EXCEPTION_RECURRING_LIMIT_INCREASE_ONLY',
  BUDGET_AMBIGUOUS_OVERLAPPING_RANGE: 'BUDGET_AMBIGUOUS_OVERLAPPING_RANGE',
  BUDGET_RECURRING_TEMPLATE_HARD_BLOCK: 'BUDGET_RECURRING_TEMPLATE_HARD_BLOCK',
  // Accounting/finance review CFO-03 (ALC-2946 B7 follow-up): resolveException
  // cascades every PENDING exception on a bill together, so a bill may only
  // ever have one open exception request at a time — otherwise approving one
  // would silently approve an unrelated, unreviewed second request.
  BUDGET_EXCEPTION_BILL_HAS_PENDING_REQUEST: 'BUDGET_EXCEPTION_BILL_HAS_PENDING_REQUEST',
} as const;

export type BudgetValidationCodeValue =
  (typeof BudgetValidationCode)[keyof typeof BudgetValidationCode];

export function reforecastPreparerUserIdsOf(budget: {
  reforecastPreparerUserIds?: string[] | null;
  reforecastPreparedByUserId?: string | null;
}): string[] {
  if (Array.isArray(budget.reforecastPreparerUserIds)) {
    return budget.reforecastPreparerUserIds;
  }
  return budget.reforecastPreparedByUserId ? [budget.reforecastPreparedByUserId] : [];
}

/** True when the actor is in the revision's material-preparer set. */
export function isReforecastSelfApprover(
  actorUserId: string,
  budget: {
    reforecastPreparerUserIds?: string[] | null;
    reforecastPreparedByUserId?: string | null;
  },
): boolean {
  return reforecastPreparerUserIdsOf(budget).includes(actorUserId);
}

export const BudgetInvalidGlAccountReason = {
  COA_VERSION_NOT_ASSIGNED: 'COA_VERSION_NOT_ASSIGNED',
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  ACCOUNT_NOT_POSTABLE: 'ACCOUNT_NOT_POSTABLE',
  ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
  VERSION_MISMATCH: 'VERSION_MISMATCH',
} as const;

export type BudgetInvalidGlAccountReason =
  (typeof BudgetInvalidGlAccountReason)[keyof typeof BudgetInvalidGlAccountReason];

export interface BudgetInvalidGlAccountErrorBody {
  code: typeof BUDGET_INVALID_GL_ACCOUNT_CODE;
  message: string;
  reason: BudgetInvalidGlAccountReason;
}

export interface BudgetActivationConflictSummary {
  budgetId: string;
  name: string;
  bookId: string;
  bookName: string;
  startPeriodId: string;
  endPeriodId: string | null;
  effectiveFrom: string;
  effectiveUntil: string | null;
}

export interface BudgetActivationSwitchRequiredBody {
  code: typeof BUDGET_ACTIVATION_SWITCH_REQUIRED_CODE;
  message: string;
  conflictingBudgets: BudgetActivationConflictSummary[];
}

export interface ListBudgetsQuery {
  entityId?: string;
  bookId?: string;
  status?: BudgetPlanStatus;
}

export interface BudgetLinePeriodLimit {
  periodId: string;
  amount: number;
}

export interface BudgetLineResponse {
  tenantId: string;
  budgetLineId: string;
  budgetId: string;
  entityId: string;
  bookId: string;
  dimensionType: BudgetDimensionType;
  costCenterId: string | null;
  departmentId: string | null;
  managerialAccountId: string | null;
  glAccountId: string | null;
  segmentId: string | null;
  segmentValueId: string | null;
  isFallback: boolean;
  startPeriodId: string;
  endPeriodId: string;
  amount: number;
  perPeriodAmount: number | null;
  periodLimits?: BudgetLinePeriodLimit[];
  currencyCode: string;
  enforcementMode: BudgetEnforcementMode;
  perPeriodEnforcementMode: BudgetEnforcementMode | null;
  alertThresholdWarning: number | null;
  alertThresholdCritical: number | null;
  status: BudgetStatus;
  createdAt: string;
  updatedAt: string | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  ownerUserId: string | null;
  ownerName?: string | null;
  ownerCanModifyLimits: boolean;
  ownerLimitMaxAmount: number | null;
  entityName?: string | null;
  bookName?: string | null;
  displayName?: string | null;
  costCenterName?: string | null;
  departmentName?: string | null;
  managerialAccountName?: string | null;
  glAccountName?: string | null;
  glAccountCode?: string | null;
  segmentName?: string | null;
  segmentValueLabel?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface CreateBudgetLineRequest {
  budgetId: string;
  entityId: string;
  bookId: string;
  dimensionType: BudgetDimensionType;
  costCenterId?: string | null;
  departmentId?: string | null;
  managerialAccountId?: string | null;
  glAccountId?: string | null;
  segmentId?: string | null;
  segmentValueId?: string | null;
  startPeriodId: string;
  endPeriodId: string;
  amount: number;
  perPeriodAmount?: number | null;
  periodLimits?: BudgetLinePeriodLimit[];
  currencyCode?: string;
  enforcementMode?: BudgetEnforcementMode;
  perPeriodEnforcementMode?: BudgetEnforcementMode | null;
  alertThresholdWarning?: number | null;
  alertThresholdCritical?: number | null;
}

export type UpdateBudgetLineRequest = Partial<CreateBudgetLineRequest> & {
  status?: BudgetStatus;
  ownerUserId?: string | null;
  ownerCanModifyLimits?: boolean;
  ownerLimitMaxAmount?: number | null;
  grantOwnerPermission?: boolean;
  /**
   * Period IDs (`YYYY-MM`) of derivation stamps the caller is authorizing to
   * drop. Each stamp displaced by a window/scope change must be named;
   * unnamed displaced stamps refuse `BUDGET_STAMPED_PERIOD_CONFLICT`.
   */
  dropStampedPeriodIds?: string[];
};

export type BudgetAmountBulkOperationMode =
  'SET' | 'INCREASE_AMOUNT' | 'DECREASE_AMOUNT' | 'INCREASE_PERCENT' | 'DECREASE_PERCENT';

export interface BudgetAmountBulkOperation {
  mode: BudgetAmountBulkOperationMode;
  value: number | null;
}

export interface BudgetBulkUpdateLinesRequest {
  budgetLineIds: string[];
  amount?: BudgetAmountBulkOperation;
  perPeriodAmount?: BudgetAmountBulkOperation;
  enforcementMode?: BudgetEnforcementMode;
  perPeriodEnforcementMode?: BudgetEnforcementMode | null;
}

export interface BudgetBulkArchiveLinesRequest {
  budgetLineIds: string[];
}

export interface BudgetUtilizationResponse {
  budgetLine: BudgetLineResponse;
  utilized: number;
  available: number;
  utilizationPct: number;
  realized: number;
  scheduled: number;
  committed: number;
  projectedAdHoc: number;
  /** Advisory total; enforcement continues to read realized + scheduled + committed only. */
  projectedAmount?: number;
  projectedBreakdown?: BudgetProjectedBreakdown;
  payrollProjectionRule?: BudgetPayrollProjectionRule | null;
  payrollProjectionRuleSetId?: string | null;
  payrollProjectionRuleSetRevision?: string | null;
  payrollProjectionCodes?: BudgetPayrollProjectionCode[];
  hiddenPayrollProjectionCount?: number;
  /** The shared per-period-limit ladder resolver's sum over the selected periods (ALC-2946, M1). */
  perPeriodLimit?: number | null;
  perPeriodUtilized?: number | null;
  perPeriodAvailable?: number | null;
  perPeriodUtilizationPct?: number | null;
}

export interface BudgetLineWithUtilizationResponse extends BudgetLineResponse {
  utilized: number;
  available: number;
  utilizationPct: number;
  realized: number;
  scheduled: number;
  committed: number;
  projectedAdHoc: number;
  projectedAmount?: number;
  projectedBreakdown?: BudgetProjectedBreakdown;
  payrollProjectionRule?: BudgetPayrollProjectionRule | null;
  payrollProjectionRuleSetId?: string | null;
  payrollProjectionRuleSetRevision?: string | null;
  payrollProjectionCodes?: BudgetPayrollProjectionCode[];
  hiddenPayrollProjectionCount?: number;
  /** The shared per-period-limit ladder resolver's sum over the selected periods (ALC-2946, M1). */
  perPeriodLimit?: number | null;
  perPeriodUtilized?: number | null;
  perPeriodAvailable?: number | null;
  perPeriodUtilizationPct?: number | null;
}

export interface BudgetExceptionResponse {
  tenantId: string;
  exceptionId: string;
  budgetLineId: string;
  billId: string | null;
  purchaseRequestId: string | null;
  purchaseOrderId: string | null;
  vendorRecurringBillId: string | null;
  /** Type requested by the requester (intent/policy hint before approval). */
  requestType: BudgetExceptionRequestType;
  /** Type effectively chosen by the approver at resolution time. */
  resolutionType: BudgetExceptionResolutionType | null;
  appliesToPeriodIds: string[];
  excessAmount: number;
  justification: string;
  status: BudgetExceptionStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdByUserId: string | null;
  createdByUserName?: string | null;
  createdAt: string;
  budgetLineDisplayName?: string | null;
  billDescription?: string | null;
  purchaseRequestTitle?: string | null;
  purchaseOrderTitle?: string | null;
  vendorRecurringBillTitle?: string | null;
  /**
   * Whether the requesting caller specifically can resolve this exception
   * (`canResolveBudgetException`, evaluated per row against that caller's
   * own userId/permissions and this line's owner/approver standing) — not a
   * property of the exception itself. Only `GET /budget/exceptions` fills
   * this in; a caller who passed the coarse
   * `BUDGET_EXCEPTION_RESOLVE_GATE_PERMISSIONS` gate (e.g. via
   * `budget:exception:read` alone, which grants visibility but not resolve
   * standing) can still fail this per-row check, which is exactly the gap
   * this field closes for adapter UIs that render approve/reject controls
   * (see that constant's own doc comment).
   */
  canResolve?: boolean;
}

export interface CreateBudgetExceptionRequest {
  budgetLineId: string;
  billId?: string;
  purchaseRequestId?: string;
  purchaseOrderId?: string;
  vendorRecurringBillId?: string;
  /** Requested handling strategy; approver may still choose final resolution type. */
  requestType?: BudgetExceptionRequestType;
  appliesToPeriodIds?: string[];
  justification: string;
}

export interface ResolveBudgetExceptionRequest {
  status: Extract<BudgetExceptionStatus, 'APPROVED' | 'REJECTED'>;
  /** Final strategy applied by approver when approving the request. */
  resolutionType?: BudgetExceptionResolutionType | null;
  resolutionNote?: string | null;
}

export interface ListBudgetLinesQuery {
  budgetId: string;
  entityId?: string;
  bookId?: string;
  periodId?: string;
  periodIds?: string[];
  status?: BudgetStatus;
}

export interface BudgetLineDetailQuery {
  periodId?: string;
  periodIds?: string[];
  eventBucket?: BudgetLineEventBucket;
  eventSearch?: string;
  eventPage?: number;
  eventPageSize?: number;
  eventSortBy?: BudgetLineEventSortBy;
  eventSortDirection?: BudgetLineEventSortDirection;
}

export type BudgetLineEventBucket = 'realized' | 'scheduled' | 'committed' | 'projected';
export type BudgetLineEventSortBy = 'date' | 'amount';
export type BudgetLineEventSortDirection = 'asc' | 'desc';

export interface BudgetCommitmentItemResponse {
  commitmentId: string;
  source: BudgetCommitmentSource;
  status: BudgetCommitmentStatus;
  periodId: string;
  committedAmount: number;
  settledAmount: number;
  openAmount: number;
  expectedDate: string | null;
  purchaseOrderId: string | null;
  purchaseOrderNumber: string | null;
  vendorRecurringBillId: string | null;
  budgetExceptionId: string | null;
  billId: string | null;
  vendorName: string | null;
}

export interface BudgetLineBillItemResponse {
  billId: string;
  vendorName: string | null;
  description: string | null;
  billNumber: string | null;
  postedAmount: number;
  periodId: string;
  journalDate: string;
  bucket: Extract<BudgetLineEventBucket, 'realized' | 'scheduled'>;
}

export interface BudgetLineProjectedBillItemResponse {
  billId: string;
  vendorName: string | null;
  description: string | null;
  billNumber: string | null;
  projectedAmount: number;
  periodId: string;
  referenceDate: string;
}

export interface BudgetLineEventItemResponse {
  payrollProjection?: BudgetPayrollProjectionItem;
  eventId: string;
  bucket: BudgetLineEventBucket;
  label: string;
  title: string;
  description: string | null;
  amount: number;
  date: string | null;
  billId: string | null;
  purchaseOrderId: string | null;
  vendorRecurringBillId: string | null;
}

export interface BudgetLineEventsResponse {
  items: BudgetLineEventItemResponse[];
  total: number;
  amountTotal: number;
  explainedCount80: number;
  explainedAmount80: number;
  page: number;
  pageSize: number;
  totalPages: number;
  bucket: BudgetLineEventBucket | null;
  sortBy: BudgetLineEventSortBy;
  sortDirection: BudgetLineEventSortDirection;
}

export interface BudgetPayrollProjectionItem {
  periodId: string;
  amount: number;
  currencyCode: string;
  sourceKind: 'PAYROLL_EMPLOYEE' | 'HEADCOUNT_POSITION';
  sourceId: string | null;
  name: string | null;
  hiringType?: 'CLT' | 'PJ';
  sourceLabel: string | null;
}

export interface BudgetLineDetailResponse {
  payrollProjectionItems?: BudgetPayrollProjectionItem[];
  budgetLine: BudgetLineResponse;
  utilized: number;
  available: number;
  utilizationPct: number;
  realized: number;
  scheduled: number;
  committed: number;
  projectedAdHoc: number;
  projectedAmount?: number;
  projectedBreakdown?: BudgetProjectedBreakdown;
  payrollProjectionRule?: BudgetPayrollProjectionRule | null;
  payrollProjectionRuleSetId?: string | null;
  payrollProjectionRuleSetRevision?: string | null;
  payrollProjectionCodes?: BudgetPayrollProjectionCode[];
  hiddenPayrollProjectionCount?: number;
  availableAfterProjected: number;
  /** The shared per-period-limit ladder resolver's sum over the selected periods (ALC-2946, M1). */
  perPeriodLimit?: number | null;
  perPeriodUtilized?: number | null;
  perPeriodAvailable?: number | null;
  perPeriodUtilizationPct?: number | null;
  commitments: BudgetCommitmentItemResponse[];
  bills: BudgetLineBillItemResponse[];
  projectedBills: BudgetLineProjectedBillItemResponse[];
  events: BudgetLineEventsResponse;
}

export interface BudgetResultCell {
  periodId: string;
  limit: number;
  realized: number;
  scheduled: number;
  committed: number;
  projected: number;
  projectedBreakdown?: BudgetProjectedBreakdown;
  payrollProjectionRule?: BudgetPayrollProjectionRule | null;
  payrollProjectionRuleSetId?: string | null;
  payrollProjectionRuleSetRevision?: string | null;
  payrollProjectionCodes?: BudgetPayrollProjectionCode[];
  hiddenPayrollProjectionCount?: number;
  available: number;
  utilizationPct: number;
}

export interface BudgetResultLine extends BudgetLineResponse {
  cells: BudgetResultCell[];
  /** The line's ceiling — always `amount` (D1, ALC-2946), regardless of any period filter. */
  totalLimit: number;
  /** Secondary/informational: Σ of the visible per-period derived cell limits. */
  totalDerivedLimit: number;
  totalRealized: number;
  totalScheduled: number;
  totalCommitted: number;
  totalProjected: number;
  projectedBreakdown?: BudgetProjectedBreakdown;
  payrollProjectionRule?: BudgetPayrollProjectionRule | null;
  payrollProjectionRuleSetId?: string | null;
  payrollProjectionRuleSetRevision?: string | null;
  payrollProjectionCodes?: BudgetPayrollProjectionCode[];
  hiddenPayrollProjectionCount?: number;
  totalAvailable: number;
  totalUtilizationPct: number;
}

export interface BudgetResultPeriodTotal {
  periodId: string;
  limit: number;
  realized: number;
  scheduled: number;
  committed: number;
  projected: number;
  projectedBreakdown?: BudgetProjectedBreakdown;
  payrollProjectionRule?: BudgetPayrollProjectionRule | null;
  payrollProjectionRuleSetId?: string | null;
  payrollProjectionRuleSetRevision?: string | null;
  payrollProjectionCodes?: BudgetPayrollProjectionCode[];
  hiddenPayrollProjectionCount?: number;
  available: number;
}

export interface BudgetRecomputeWarningRecurringTemplate {
  vendorRecurringBillId: string;
  name: string;
}

export interface BudgetRecomputeWarning {
  code: 'RECURRING_TEMPLATE_CLASSIFICATION_INVALID';
  message: string;
  recurringTemplates: BudgetRecomputeWarningRecurringTemplate[];
}

export interface BudgetResultsResponse {
  payrollUnallocatedAmount?: number;
  payrollScenario?: BudgetPayrollScenarioResponse;
  budgetId: string;
  periodIds: string[];
  lines: BudgetResultLine[];
  periodTotals: BudgetResultPeriodTotal[];
  /** The caller cannot view payroll-backed amounts included in the underlying budget ledger. */
  payrollDataRestricted?: boolean;
  recomputeStatus?: BudgetRecomputeStatus;
  lastRecomputedAt?: string | null;
  recomputeWarnings?: BudgetRecomputeWarning[];
}

export interface BudgetResultsQuery {
  periodIds?: string[];
  /** Keep only lines whose total limit is at least this value; period totals follow the filter. */
  minTotal?: number;
}

export interface ListBudgetExceptionsQuery {
  status?: BudgetExceptionStatus;
  billId?: string;
  purchaseRequestId?: string;
  purchaseOrderId?: string;
}

export type BudgetCheckAction = 'PASS' | 'WARN' | 'WARN_CRITICAL' | 'REQUIRE_EXCEPTION' | 'BLOCK';

export type BudgetLimitScope = 'RANGE' | 'PERIOD';

export interface BudgetCheckMatch {
  budgetLineId: string;
  limitScope: BudgetLimitScope;
  classificationId: string | null;
  lineItemId: string | null;
  splitId: string | null;
  dimensionType: BudgetDimensionType;
  costCenterId: string | null;
  departmentId: string | null;
  managerialAccountId: string | null;
  glAccountId: string | null;
  segmentId: string | null;
  segmentValueId: string | null;
  periodId: string;
  splitAmount: number;
  budgetAmount: number;
  utilized: number;
  projected: number;
  projectedAdHoc: number;
  projectedTotal: number;
  available: number;
  availableAfterProjected: number;
  utilizationPct: number;
  action: BudgetCheckAction;
  excessAmount: number;
  warningThreshold: number | null;
  criticalThreshold: number | null;
  enforcementMode: BudgetEnforcementMode;
}

export const BudgetCheckSkipReason = {
  NO_ACTIVE_BUDGET: 'NO_ACTIVE_BUDGET',
  AMBIGUOUS_ACTIVE_BOOK: 'AMBIGUOUS_ACTIVE_BOOK',
  /**
   * The active budget's recompute never completed and `blockEnforcementWhileStale`
   * is on: `action` is BLOCK with no matches (there is nothing per-key to report —
   * the whole check refused to trust current utilization). A writer that only reads
   * `matches` to build a blocked-key set must check this explicitly and refuse to
   * write anything, the same way it already must for AMBIGUOUS_ACTIVE_BOOK.
   */
  BLOCKED_WHILE_STALE: 'BLOCKED_WHILE_STALE',
} as const;

export type BudgetCheckSkipReason =
  (typeof BudgetCheckSkipReason)[keyof typeof BudgetCheckSkipReason];

export interface BudgetCheckResult {
  action: BudgetCheckAction;
  matches: BudgetCheckMatch[];
  /** Set when enforcement intentionally skips control (e.g. ambiguous active book). */
  skipReason?: BudgetCheckSkipReason;
}

export interface BudgetHierarchyViolationDetails {
  parentBudgetLineId: string;
  parentAmount: number;
  childrenSum: number;
  violatingBudgetLineId: string | null;
  siblingBudgetLineIds: string[];
}

/** Controller gate: user must hold at least one workflow permission before requesting an exception. */
export const BUDGET_EXCEPTION_CREATE_GATE_PERMISSIONS = [
  'ap:bill:submit',
  'ap:purchase-request:submit',
  'ap:purchase-request:approve',
  'ap:purchase-order:mark-ready',
  'party:manage',
] as const;

/**
 * Controller gate for both listing and resolving budget exceptions. A
 * budget-line owner or a `budgetExceptionApproverUserIds` entry can act
 * through `canResolveBudgetException` without holding any of these — that
 * grant is a plain tenant-settings list, never registered with WorkOS, so
 * per rbac-workos-lifecycle.md §0.1 a permission-less caller cannot be
 * treated as self-scoped through it (their standing isn't tied to their own
 * `userId` alone; a tenant-wide approver's rows are other users' data).
 * Requiring one of these real, registered permissions closes that gap at
 * the cost of a real limitation: a configured approver holding none of them
 * gets no inline row in Laboratório and must resolve from the budget page
 * directly, where `canResolveBudgetException` still authorizes them. See
 * ALC-2369's PR discussion for why the alternative (a dedicated
 * cross-user-approver `PermissionKey`, reconciled through WorkOS) was left
 * for a separate change instead of folded into this one.
 *
 * Deliberately excludes `budget:line:read` (Charlie's I-03 finding): that
 * permission never correlates with `canResolveBudgetException` at all (it
 * checks `budget:line:manage`, ownerUserId, or the approver list — never
 * `budget:line:read`), so including it here would only grant tenant-wide
 * visibility into every pending exception's justification, excess amount,
 * and requester identity to a caller who can never actually act on any of
 * them — pure over-exposure with no offsetting capability.
 */
export const BUDGET_EXCEPTION_RESOLVE_GATE_PERMISSIONS = [
  'budget:line:manage',
  'budget:exception:read',
] as const;

function hasBudgetExceptionPermission(
  userPermissions: readonly string[],
  permission: string,
): boolean {
  return userPermissions.includes('*') || userPermissions.includes(permission);
}

/** Whether the user may request an exception for the target document type. */
export function canCreateBudgetException(
  userPermissions: readonly string[],
  input: Pick<
    CreateBudgetExceptionRequest,
    'billId' | 'purchaseRequestId' | 'purchaseOrderId' | 'vendorRecurringBillId'
  >,
): boolean {
  if (input.billId) {
    return hasBudgetExceptionPermission(userPermissions, 'ap:bill:submit');
  }
  if (input.purchaseRequestId) {
    return (
      hasBudgetExceptionPermission(userPermissions, 'ap:purchase-request:submit') ||
      hasBudgetExceptionPermission(userPermissions, 'ap:purchase-request:approve')
    );
  }
  if (input.purchaseOrderId) {
    return hasBudgetExceptionPermission(userPermissions, 'ap:purchase-order:mark-ready');
  }
  if (input.vendorRecurringBillId) {
    return hasBudgetExceptionPermission(userPermissions, 'party:manage');
  }
  return false;
}

/** Whether the user may approve or reject an exception on a budget line. */
export function canResolveBudgetException(
  userPermissions: readonly string[],
  context: {
    userId: string;
    budgetLineOwnerUserId: string | null;
    budgetExceptionApproverUserIds: readonly string[];
  },
): boolean {
  if (hasBudgetExceptionPermission(userPermissions, 'budget:line:manage')) {
    return true;
  }
  if (context.budgetLineOwnerUserId != null && context.budgetLineOwnerUserId === context.userId) {
    return true;
  }
  if (context.budgetExceptionApproverUserIds.includes(context.userId)) {
    return true;
  }
  return false;
}
